import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Grid,
} from "@mui/material";
import CustomTypography from "../../../components/common/form/CustomTypography";
import CustomTextField from "../../../components/common/textFields/TextField";
import AppSearchDropDown2 from "../../../components/common/filter/AppSearchDropdown2";
import AppButton from "../../../components/common/buttons/AppButton";

interface CreateInventoryPopupProps {
  open: boolean;
  onClose: () => void;
  onCreate: (data: {
    customerId: number;
    orderId: number;
    type: number;
    label: string;
  }) => void;
  labelInputText?: string;
  orderId: number;
  customerId: number;
}

export default function CreateInventoryPopup({
  open,
  onClose,
  onCreate,
  labelInputText = "Inventory Label",
  orderId,
  customerId,
}: CreateInventoryPopupProps) {
  const theme = useTheme();

  const [inventoryType, setInventoryType] = useState<any | null>(null);
  const [inventoryLabel, setInventoryLabel] = useState("");

  const handleCreate = () => {
    if (!inventoryLabel || !inventoryType) {
      alert("Please select the inventory type and enter the inventory label.");
      return;
    }

    const typeMap: Record<string, number> = {
      master: 1,
      new: 2,
    };

    const type = typeMap[inventoryType.id];

    const data = {
      customerId,
      orderId,
      type,
      label: inventoryLabel,
    };

    onCreate(data);
    setInventoryType(null);
    setInventoryLabel("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "@media (max-width:600px)": {
          width: "100vw",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 4,
          pt: 4,
          pb: 3,
        }}
      >
        <CustomTypography variant="heading4">Create Inventory</CustomTypography>
      </DialogTitle>

      <DialogContent sx={{ px: 4, pt: 1, pb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <CustomTypography
              variant="heading7"
              color={theme.palette.customColors.greyTextColor}
              style={{ marginBottom: 14 }}
            >
              Inventory Type
            </CustomTypography>
            <AppSearchDropDown2
              title="Select Inventory Type"
              isMultiSelect={false}
              value={inventoryType ? [inventoryType] : []}
              list={[
                { id: "master", value: "Master Inventory" },
                { id: "new", value: "New Inventory" },
              ]}
              handleSelect={(selectedItems) =>
                setInventoryType(selectedItems[0] || null)
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTypography
              variant="heading7"
              color={theme.palette.customColors.greyTextColor}
              style={{ marginBottom: 14 }}
            >
              Inventory Label
            </CustomTypography>
            <CustomTextField
              borderColor={`0.1px solid ${theme.palette.customColors.greyDarkborder}`}
              value={inventoryLabel}
              placeholder={labelInputText}
              width="100%"
              type="text"
              onChange={(e) => setInventoryLabel(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 3, justifyContent: "flex-end", gap: 1 }}>
        <AppButton
          onClick={onClose}
          label="Cancel"
          fontWeight={600}
          customStyles={{
            height: "2.2rem",
            minWidth: "5.438rem",
            borderRadius: "4px",
            border: `1px solid ${theme.palette.customColors.greyTextColor}`,
            backgroundColor: theme.palette.common.white,
            color: theme.palette.customColors.greyTextColor,
          }}
          variant="grayscale"
        />
        <AppButton
          label="Create"
          onClick={handleCreate}
          fontWeight={600}
          customStyles={{
            height: "2.2rem",
            minWidth: "5.438rem",
            borderRadius: "4px",
            backgroundColor: theme.palette.customColors.pinkDark,
            color: theme.palette.common.white,
          }}
          variant="secondary"
        />
      </DialogActions>
    </Dialog>
  );
}
